import { useMemo, useState } from 'react';
import type { Sheet, PlacedPart } from '@/api/nesting';

interface SheetLayoutDiagramProps {
  sheet: Sheet;
  sheetIndex: number;
  maxWidth?: number;
}

/** Deterministic color from area name using golden-angle HSL spacing */
function areaColor(areaName: string, areaIndex: number): string {
  const hue = (areaIndex * 137.5) % 360;
  return `hsla(${hue}, 55%, 45%, 0.75)`;
}

/** Compute used area for efficiency */
function sheetEfficiency(sheet: Sheet): number {
  const totalArea = sheet.width * sheet.height;
  if (totalArea <= 0) return 0;
  let usedArea = 0;
  for (const p of sheet.placements) {
    usedArea += p.width * p.height;
  }
  return Math.round((usedArea / totalArea) * 1000) / 10;
}

/** Truncate label to fit within part rect */
function fitLabel(label: string, maxChars: number): string {
  if (label.length <= maxChars) return label;
  return label.slice(0, maxChars - 1) + '…';
}

export default function SheetLayoutDiagram({
  sheet,
  sheetIndex,
  maxWidth = 580,
}: SheetLayoutDiagramProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  // Scale factor to fit within maxWidth
  const scale = maxWidth / sheet.width;
  const svgW = maxWidth;
  const svgH = sheet.height * scale;

  // Build area color palette
  const areaColors = useMemo(() => {
    const areas = [...new Set(sheet.placements.map((p) => p.part.sourceArea))];
    const palette: Record<string, string> = {};
    areas.forEach((area, i) => {
      palette[area] = areaColor(area, i);
    });
    return palette;
  }, [sheet.placements]);

  const efficiency = sheetEfficiency(sheet);
  const patternId = `hatch-${sheetIndex}`;

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-1.5 px-1">
        <span className="text-[var(--text-secondary)] text-xs font-medium">
          Sheet {sheetIndex + 1}
        </span>
        <span
          className="text-xs font-mono"
          style={{
            color:
              efficiency >= 75
                ? 'var(--success)'
                : efficiency >= 50
                  ? 'var(--warning)'
                  : 'var(--danger)',
          }}
        >
          {efficiency}% used
        </span>
      </div>

      <svg
        width={svgW}
        height={svgH}
        viewBox={`0 0 ${sheet.width} ${sheet.height}`}
        className="rounded border"
        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-panel)' }}
      >
        {/* Hatch pattern for waste areas */}
        <defs>
          <pattern
            id={patternId}
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
          >
            <path
              d="M0,6 l6,-6 M-1.5,1.5 l3,-3 M4.5,7.5 l3,-3"
              stroke="var(--text-muted)"
              strokeWidth="0.3"
              opacity="0.25"
            />
          </pattern>
        </defs>

        {/* Sheet background with hatch */}
        <rect
          x={0}
          y={0}
          width={sheet.width}
          height={sheet.height}
          fill={`url(#${patternId})`}
        />

        {/* Placed parts */}
        {sheet.placements.map((p, i) => {
          const isHovered = hoveredIdx === i;
          // Approximate label char budget based on part size
          const charBudget = Math.floor(p.width / 1.2);
          const shortLabel = fitLabel(
            p.part.label.split(' - ').pop() || p.part.label,
            Math.max(3, charBudget),
          );
          const fontSize = Math.min(
            Math.max(1.2, Math.min(p.width, p.height) * 0.13),
            3,
          );
          const showLabel = p.width > 3 && p.height > 2;
          const showDims = p.width > 5 && p.height > 4;

          return (
            <g
              key={i}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ cursor: 'default' }}
            >
              <rect
                x={p.x}
                y={p.y}
                width={p.width}
                height={p.height}
                fill={areaColors[p.part.sourceArea] || 'hsla(200, 50%, 45%, 0.7)'}
                stroke={isHovered ? '#fff' : 'rgba(0,0,0,0.3)'}
                strokeWidth={isHovered ? 0.6 : 0.3}
                rx={0.3}
              />
              {showLabel && (
                <text
                  x={p.x + p.width / 2}
                  y={p.y + p.height / 2 - (showDims ? fontSize * 0.4 : 0)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontSize={fontSize}
                  fontWeight={500}
                  style={{ pointerEvents: 'none' }}
                >
                  {shortLabel}
                </text>
              )}
              {showDims && (
                <text
                  x={p.x + p.width / 2}
                  y={p.y + p.height / 2 + fontSize * 0.8}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="rgba(255,255,255,0.7)"
                  fontSize={fontSize * 0.75}
                  style={{ pointerEvents: 'none' }}
                >
                  {p.width.toFixed(1)}&quot; × {p.height.toFixed(1)}&quot;
                </text>
              )}
            </g>
          );
        })}

        {/* Sheet dimensions label */}
        <text
          x={sheet.width - 0.5}
          y={sheet.height - 0.5}
          textAnchor="end"
          fill="var(--text-muted)"
          fontSize="1.5"
          opacity="0.6"
        >
          {sheet.width}&quot; × {sheet.height}&quot;
        </text>
      </svg>

      {/* Tooltip */}
      {hoveredIdx !== null && sheet.placements[hoveredIdx] && (
        <div
          className="absolute z-10 p-2 rounded text-xs shadow-lg pointer-events-none"
          style={{
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            top: 4,
            right: 4,
          }}
        >
          <div className="font-medium">{sheet.placements[hoveredIdx].part.label}</div>
          <div style={{ color: 'var(--text-muted)' }}>
            {sheet.placements[hoveredIdx].width.toFixed(2)}&quot; ×{' '}
            {sheet.placements[hoveredIdx].height.toFixed(2)}&quot;
            {sheet.placements[hoveredIdx].rotated ? ' (rotated)' : ''}
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            Area: {sheet.placements[hoveredIdx].part.sourceArea}
          </div>
        </div>
      )}
    </div>
  );
}
